import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import Select from "react-select"

import { GET, POST, PUT, httpOk } from "helpers/api"
import ChequePreviewPanel from "./ChequePreviews"

const FIELD_LABELS = {
	show_logo: "Показать логотип",
	show_pos_name: "Показать название торговой точки",
	show_pos_phone: "Показать телефон торговой точки",
	show_pos_address: "Показать адрес торговой точки",
	show_cashier_name: "Показать имя кассира",
	show_waiter_name: "Показать имя официанта",
	show_courier_name: "Показать имя курьера",
	show_hall_name: "Показать название зала",
	show_table_name: "Показать название стола",
	show_cheque_id: "Показать ID чека",
	show_cheque_number: "Показать номер чека",
	show_guests_count: "Показать кол-во гостей",
	show_tin: "Показать ИНН",
	show_order_type: "Показать тип заказа",
	show_cheque_opened_at: "Показать время открытия чека",
	show_cheque_printed_at: "Показать время печати чека",
	show_cheque_closed_at: "Показать время закрытия чека",
	show_debt_info: "Показать блок долга",
	is_split_by_persons: "Показывать деление на персон",
	show_feedback_qr: "Показать QR код для отзыва",
	show_loyalty_qr: "Показать QR кэшбэка",
	show_row_number: "Нумерация позиций",
	is_service_charge_visible: "Показать обслуживание под продуктом",
	show_vat: "Показать НДС",
	show_fiscal_code: "Показать ИКПУ",
	cheque_products_font: "Шрифт таблицы продуктов (чек)",
	kitchen_new_order: "Заголовок «Новый заказ»",
	kitchen_date: "Дата заказа",
	kitchen_order_type: "Тип заказа",
	kitchen_table_info: "Зал / стол",
	kitchen_sale_price: "Показать цену продажи",
	kitchen_row_number: "Нумерация позиций",
	kitchen_products_font: "Шрифт таблицы продуктов (кухня)",
	kitchen_cheque_number: "Показать номер чека",
	kitchen_comment: "Комментарий в кухню",
	kitchen_cheque_comment: "Комментарий к чеку (кухня)",
	reservation_table_info: "Стол",
	reservation_datetime: "Дата и время",
	reservation_guests_count: "Количество гостей",
	reservation_client_name: "Имя клиента",
	reservation_user_name: "Имя сотрудника",
	reservation_pos_phone: "Телефон ресторана",
	reservation_pos_note: "Примечание ресторана",
	reservation_client_phone: "Телефон клиента",
	reservation_client_note: "Примечание клиента",
	reservation_prepaid: "Предоплата",
	reservation_products: "Продукты предзаказа",
	reservation_products_font: "Размер шрифта блока продуктов",
}

const PROMO_TEXT_FIELD_KEYS = ["show_promo_text_pre_cheque", "show_promo_text_post_cheque"]

/** Типы документа берутся только из ответа GET /cheque-templates/defaults (и при смене value). */
function extractDocumentTypesFromDefaultsRecord(record) {
	if (!record || typeof record !== "object") return []
	if (Array.isArray(record.document_types)) return record.document_types
	if (Array.isArray(record.types)) return record.types
	if (Array.isArray(record)) return record
	if (record.value != null || record.name != null || record.id != null) {
		return [{ id: record.id, value: record.value, name: record.name }]
	}
	return []
}

function defaultItem() {
	return {
		name: "",
		comment: "",
		is_active: true,
		is_default: true,
		pos_id: null,
		pos_ids: [],
		value: "cheque",
		groups: [],
	}
}

function unwrapRecord(response) {
	const d = response?.data
	if (d == null) return null
	if (typeof d === "object" && d.data !== undefined && !Array.isArray(d.data)) return d.data
	return d
}

function normalizePosListResponse(response) {
	const raw = response?.data
	if (Array.isArray(raw)) return raw
	if (Array.isArray(raw?.data)) return raw.data
	return []
}

function sortFields(fields) {
	if (!Array.isArray(fields)) return []
	const hasPosition = (f) => f.position != null && f.position !== ""
	const orderRank = (f) => {
		const o = f.order_number
		if (o == null || o === "") return 1_000_000
		return Number(o)
	}
	return [...fields].sort((a, b) => {
		if (hasPosition(a) || hasPosition(b)) {
			const pa = hasPosition(a) ? Number(a.position) : Number.MAX_SAFE_INTEGER
			const pb = hasPosition(b) ? Number(b.position) : Number.MAX_SAFE_INTEGER
			if (pa !== pb) return pa - pb
			const oa = orderRank(a)
			const ob = orderRank(b)
			if (oa !== ob) return oa - ob
			return (a.id ?? 0) - (b.id ?? 0)
		}
		const oa = a.order_number ?? a.id ?? 0
		const ob = b.order_number ?? b.id ?? 0
		return oa - ob
	})
}

/** Каталог: true — показать блок; false | null — не показывать; undefined — старый API (legacy). */
function hasEditorSection(flag, legacyWhenUnspecified) {
	if (flag === true) return true
	if (flag === false || flag === null) return false
	return !!legacyWhenUnspecified
}

/** Группы, где при отсутствии `possible_values.font_size` подставляем стандартный набор (каталог / старый API). */
const GROUPS_WITH_DEFAULT_FONT_SIZES = new Set(["cheque_products", "kitchen", "reservation"])
const DEFAULT_FONT_SIZE_OPTIONS = ["small", "medium", "large"]

function addDefaultFontSizesToGroups(groups) {
	if (!Array.isArray(groups)) return []
	return groups.map((g) => {
		if (!GROUPS_WITH_DEFAULT_FONT_SIZES.has(g.value)) return g
		return {
			...g,
			fields: (g.fields || []).map((f) => {
				if (f.has_font_size === false || f.has_font_size === null) return f
				const existing = f.possible_values?.font_size
				if (Array.isArray(existing) && existing.length > 0) return f
				return {
					...f,
					possible_values: {
						...(f.possible_values || {}),
						font_size: [...DEFAULT_FONT_SIZE_OPTIONS],
					},
				}
			}),
		}
	})
}

/** Только стили (напр. размер шрифта таблицы продуктов), без is_show в API */
function fieldUsesVisibilityToggle(field) {
	const key = field?.field_key
	if (!key) return true
	if (key.endsWith("_products_font")) return false
	// Поле только про размер (заголовок кухонного чека): выключить его показ нельзя
	if (field.input_type === "font_size") return false
	return true
}

/** В каталоге/шаблоне пришло явное `is_show` (не рисуем ✓/×, если поля нет — как у `cheque_products_font`). */
function fieldHasExplicitIsShow(field) {
	return field?.is_show !== undefined && field?.is_show !== null
}

/** Видимость в превью: явный флаг или для boolean/textarea — по умолчанию «показывать». */
function fieldPreviewIsShown(field) {
	if (!fieldUsesVisibilityToggle(field)) return undefined
	if (fieldHasExplicitIsShow(field)) return !!field.is_show
	if (field.input_type === "boolean" || field.input_type === "textarea") {
		if (field.default_is_show !== undefined && field.default_is_show !== null) {
			return !!field.default_is_show
		}
		return true
	}
	return false
}

/** Иконка ✓/× в списке полей — только при явном `is_show` в данных API. */
function shouldShowVisibilityStatusIcon(field) {
	if (!fieldUsesVisibilityToggle(field)) return false
	if (!hasEditorSection(field.has_is_show, true)) return false
	return fieldHasExplicitIsShow(field)
}

function sanitizeGroupsForSave(groups) {
	return (groups || []).map((g) => ({
		...g,
		fields: (g.fields || []).map((f) => {
			const {
				possible_values,
				input_type,
				text_placeholder,
				text_max_length,
				has_align,
				has_font_size,
				has_order_number,
				has_text_value,
				has_is_show,
				default_is_show,
				default_order_number,
				...rest
			} = f
			return rest
		}),
	}))
}

function alignLabel(v) {
	if (v === "center") return "По центру"
	if (v === "right") return "Справа"
	return "Слева"
}

function fontLabel(v) {
	if (v === "large") return "Крупный"
	if (v === "medium") return "Средний"
	return "Мелкий"
}

function posStatusLabel(item, t) {
	if (item.status === 1) return t("activated")
	if (item.status === -1) return t("disconnected")
	if (item.status === -3) return t("deleted")
	return "—"
}

function CreateUpdate() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [data, setData] = useState(defaultItem())
	const [activeGroupValue, setActiveGroupValue] = useState("")
	const [selectedFieldKey, setSelectedFieldKey] = useState(null)

	const activeGroup = (data.groups || []).find((g) => g.value === activeGroupValue)

	const sortedFields = sortFields(activeGroup?.fields)

	const selectedField =
		selectedFieldKey && activeGroup
			? activeGroup.fields?.find((f) => f.field_key === selectedFieldKey) || null
			: null

	const [mainActiveTab, setMainActiveTab] = useState(1)
	const [documentTypeOptions, setDocumentTypeOptions] = useState([])
	const [posList, setPosList] = useState([])
	const [posSearch, setPosSearch] = useState("")

	const loadPosList = useCallback(async () => {
		const response = await GET("/pos", {}, { loader: false })
		if (httpOk(response)) setPosList(normalizePosListResponse(response))
	}, [])

	async function applyDefaultsForDocumentType(nextValue) {
		const response = await GET("/cheque-templates/defaults", { value: nextValue }, { loader: true })
		if (!httpOk(response)) return
		const record = unwrapRecord(response) || {}
		const groupsRaw = Array.isArray(record.groups) ? JSON.parse(JSON.stringify(record.groups)) : []
		const groups = addDefaultFontSizesToGroups(groupsRaw)
		const typesFromApi = extractDocumentTypesFromDefaultsRecord(record)
		if (typesFromApi.length) setDocumentTypeOptions(typesFromApi)
		setData((prev) => ({
			...prev,
			value: record.value ?? nextValue,
			groups,
		}))
		const firstG = groups[0]
		if (firstG) {
			setActiveGroupValue(firstG.value)
			const sf = sortFields(firstG.fields)
			setSelectedFieldKey(sf[0]?.field_key ?? null)
		}
	}

	async function loadNew() {
		const response = await GET("/cheque-templates/defaults", {}, { loader: true })
		if (httpOk(response)) {
			const record = unwrapRecord(response) || {}
			const groupsRaw = Array.isArray(record.groups) ? JSON.parse(JSON.stringify(record.groups)) : []
			const groups = addDefaultFontSizesToGroups(groupsRaw)
			setData({
				...defaultItem(),
				...record,
				name: record.name ?? "",
				comment: record.comment ?? "",
				is_active: record.is_active !== false,
				is_default: !!record.is_default,
				pos_id: record.pos_id ?? null,
				pos_ids: Array.isArray(record.pos_ids) ? [...record.pos_ids] : [],
				value: record.value ?? "cheque",
				groups,
			})
			setDocumentTypeOptions(extractDocumentTypesFromDefaultsRecord(record))
			const firstG = groups[0]
			if (firstG) {
				setActiveGroupValue(firstG.value)
				const sf = sortFields(firstG.fields)
				setSelectedFieldKey(sf[0]?.field_key ?? null)
			}
		} else {
			setData(defaultItem())
		}
	}

	async function loadEdit() {
		const response = await GET(`/cheque-templates/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			const record = unwrapRecord(response) || {}
			const groupsRaw = Array.isArray(record.groups) ? JSON.parse(JSON.stringify(record.groups)) : []
			const groups = addDefaultFontSizesToGroups(groupsRaw)
			setData({
				...defaultItem(),
				...record,
				groups,
				pos_id: record.pos_id ?? null,
				pos_ids: Array.isArray(record.pos_ids) ? [...record.pos_ids] : [],
				value: record.value ?? "cheque",
			})
			setDocumentTypeOptions(extractDocumentTypesFromDefaultsRecord(record))
			const firstG = groups[0]
			if (firstG) {
				setActiveGroupValue(firstG.value)
				const sf = sortFields(firstG.fields)
				setSelectedFieldKey(sf[0]?.field_key ?? null)
			}
		}
	}

	const posSearchLower = posSearch.trim().toLowerCase()
	const filteredPosList = !posSearchLower
		? posList
		: posList.filter(
			(p) =>
				String(p.name || "")
					.toLowerCase()
					.includes(posSearchLower) ||
				String(p.address || "")
					.toLowerCase()
					.includes(posSearchLower) ||
				String(p.id ?? "").includes(posSearchLower)
		)

	function updateField(groupValue, fieldKey, patch) {
		setData((prev) => ({
			...prev,
			groups: (prev.groups || []).map((g) => {
				if (g.value !== groupValue) return g
				return {
					...g,
					fields: (g.fields || []).map((f) =>
						f.field_key === fieldKey ? { ...f, ...patch } : f
					),
				}
			}),
		}))
	}

	function onSelectGroup(value) {
		setActiveGroupValue(value)
	}

	function togglePosId(posId) {
		const id = Number(posId)
		if (Number.isNaN(id)) return
		setData((prev) => {
			const set = new Set(prev.pos_ids || [])
			if (set.has(id)) set.delete(id)
			else set.add(id)
			return { ...prev, pos_ids: Array.from(set) }
		})
	}

	function setAllFilteredChecked(checked) {
		const ids = filteredPosList.map((p) => p.id).filter((x) => x != null)
		setData((prev) => {
			const set = new Set(prev.pos_ids || [])
			if (checked) ids.forEach((i) => set.add(Number(i)))
			else ids.forEach((i) => set.delete(Number(i)))
			return { ...prev, pos_ids: Array.from(set) }
		})
	}

	const selectedPosCount = (data.pos_ids || []).length
	const allFilteredSelected =
		filteredPosList.length > 0 &&
		filteredPosList.every((p) => (data.pos_ids || []).map(Number).includes(Number(p.id)))

	function buildPayload() {
		const payload = {
			name: data.name,
			comment: data.comment ?? "",
			is_active: !!data.is_active,
			is_default: !!data.is_default,
			pos_id: data.pos_id ?? null,
			pos_ids: Array.isArray(data.pos_ids) ? data.pos_ids.map(Number).filter((n) => !Number.isNaN(n)) : [],
			value: data.value,
			groups: sanitizeGroupsForSave(data.groups),
		}
		delete payload.created_at
		delete payload.updated_at
		delete payload.row_number
		if (!id) delete payload.id
		return payload
	}

	async function createUpdate(e) {
		e.preventDefault()
		const payload = buildPayload()

		let response
		if (id) {
			response = await PUT(`/cheque-templates/${id}`, payload, { loader: true })
		} else {
			response = await POST("/cheque-templates", payload, { loader: true })
		}

		if (httpOk(response)) return
	}

	const alignOptions = selectedField?.possible_values?.align || []
	const fontOptions = selectedField?.possible_values?.font_size || []
	const isKitchenGroup = activeGroupValue === "kitchen"

	const showAlignSection =
		!isKitchenGroup &&
		selectedField &&
		hasEditorSection(selectedField.has_align, alignOptions.length > 0)
	const showFontSection =
		selectedField && hasEditorSection(selectedField.has_font_size, fontOptions.length > 0)
	const showAlignFont = showAlignSection || showFontSection

	const isTextareaField =
		selectedField &&
		(selectedField.input_type === "textarea" ||
			PROMO_TEXT_FIELD_KEYS.includes(selectedField.field_key))

	const legacyOrderNumber =
		!!selectedField &&
		(selectedField.order_number != null || selectedField.default_order_number != null)

	const showOrderInput =
		!isKitchenGroup &&
		selectedField &&
		!isTextareaField &&
		!selectedField.field_key?.endsWith("_products_font") &&
		hasEditorSection(selectedField.has_order_number, legacyOrderNumber)

	const showTextValueInput =
		selectedField &&
		hasEditorSection(
			selectedField.has_text_value,
			selectedField.input_type === "textarea" || PROMO_TEXT_FIELD_KEYS.includes(selectedField.field_key)
		)

	const textMaxLen = (() => {
		if (!selectedField || selectedField.text_max_length == null) return undefined
		const n = Number(selectedField.text_max_length)
		return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
	})()

	const alignSelectOptions = (selectedField?.possible_values?.align || []).map((a) => ({
		value: a,
		label: alignLabel(a),
	}))

	const selectedAlignOption =
		alignSelectOptions.length > 0
			? alignSelectOptions.find((o) => o.value === (selectedField?.align ?? "left")) ??
			alignSelectOptions[0]
			: null

	const fontSelectOptions = (selectedField?.possible_values?.font_size || []).map((fs) => ({
		value: fs,
		label: fontLabel(fs),
	}))

	const selectedFontOption =
		fontSelectOptions.length > 0
			? fontSelectOptions.find((o) => o.value === (selectedField?.font_size ?? "small")) ??
			fontSelectOptions[0]
			: null

	const typeSelectItems =
		documentTypeOptions.length > 0
			? documentTypeOptions
			: data?.value != null || data?.name != null
				? [{ id: data.id, value: data.value, name: data.name }]
				: []

	let selectedDocumentTypeOption = null
	if (data?.value !== undefined && data?.value !== null && data?.value !== "") {
		const match = typeSelectItems.find((o) => String(o.value) === String(data.value))
		selectedDocumentTypeOption =
			match ??
			({
				id: data.document_type_id ?? data.type_id ?? data.id,
				value: data.value,
				name:
					data.type_name ??
					data.document_type_name ??
					data.name ??
					String(data.value),
			})
	}

	const documentTypeSelectOptions =
		selectedDocumentTypeOption &&
			!typeSelectItems.some((o) => String(o.value) === String(selectedDocumentTypeOption.value))
			? [...typeSelectItems, selectedDocumentTypeOption]
			: typeSelectItems

	useEffect(() => {
		if (id) loadEdit()
		else loadNew()
	}, [id]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		loadPosList()
	}, [loadPosList])

	useEffect(() => {
		if (!activeGroupValue || !(data.groups || []).length) return
		const g = data.groups.find((x) => x.value === activeGroupValue)
		if (!g) return
		const sf = sortFields(g.fields)
		setSelectedFieldKey((prev) => {
			if (prev && sf.some((f) => f.field_key === prev)) return prev
			return sf[0]?.field_key ?? null
		})
	}, [activeGroupValue, data.groups])

	useEffect(() => {
		if (activeGroupValue || !(data.groups || []).length) return
		const first = data.groups[0]
		setActiveGroupValue(first.value)
	}, [data.groups, activeGroupValue])

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-2">
				<div className="d-flex">
					<h4 className="vertical-center">{t('template')}</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						{!id ?
							<h6>{t('create')}</h6>
							:
							<h6>{t('edit')}</h6>
						}
					</div>
				</div>
				<div className="d-flex gap-2 flex-wrap">
					<button type="button" className="btn btn-outline-warning"
						onClick={() => navigate("/cheque-templates")}>
						{t("cancel")}
					</button>
					<button type="submit" form="cheque-template-main-form" className="btn btn-primary">
						{t("save")}
					</button>
				</div>
			</div>

			<form id="cheque-template-main-form" onSubmit={createUpdate} autoComplete="off">
				<div className="tabs tabs-sm mb-2">
					<ul>
						<li className={mainActiveTab === 1 ? "active" : ""} onClick={() => setMainActiveTab(1)}>
							<span className="link">{t("settings")}</span>
						</li>
						<li className={mainActiveTab === 2 ? "active" : ""} onClick={() => setMainActiveTab(2)}>
							<span className="link">{t("general")}</span>
						</li>
						<li className={mainActiveTab === 3 ? "active" : ""} onClick={() => setMainActiveTab(3)}>
							<span className="link">{t("poses")}</span>
						</li>
					</ul>
				</div>

				<div className={mainActiveTab !== 1 ? "d-none" : ""}>
					<div className="card mb-3">
						<div className="card-body">
							<div className="row g-3">
								<div className="col-md-6 col-lg-5">
									<div className="form-group">
										<label>Тип документа</label>
										<Select
											isClearable={!id}
											isDisabled={!!id}
											options={documentTypeSelectOptions}
											value={selectedDocumentTypeOption}
											onChange={(option) => {
												if (!option) {
													setData((prev) => ({ ...prev, value: null }))
													return
												}
												if (!id) void applyDefaultsForDocumentType(option.value)
												else setData((prev) => ({ ...prev, value: option.value }))
											}}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											getOptionLabel={(option) => option.name ?? option.value}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="d-flex flex-wrap gap-2 mb-3">
						{(data.groups || []).map((g) => (
							<button
								key={g.value}
								type="button"
								className={`btn btn-sm ${activeGroupValue === g.value ? "btn-primary" : "btn-outline-primary"}`}
								onClick={() => onSelectGroup(g.value)}>
								{g.name || g.value}
							</button>
						))}
					</div>

					<div
						className="d-flex gap-3 align-items-stretch cheque-template-editor-row"
						style={{
							height: "calc(100vh - 300px)",
							minHeight: "420px",
							marginBottom: "24px",
							paddingBottom: "8px",
						}}>
						<div
							style={{
								minWidth: "320px",
								maxWidth: "380px",
								flex: "0 0 360px",
								height: "100%",
								minHeight: 0,
							}}>
							<div className="card h-100 mb-0" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
								<div className="card-body p-0" style={{ overflowY: "auto", flex: 1, minHeight: 0, paddingBottom: "12px" }}>
									<div className="list-group list-group-flush">
										{sortedFields.map((f) => (
											<button
												key={f.field_key}
												type="button"
												className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 ${selectedFieldKey === f.field_key ? "border border-primary bg-light" : ""
													}`}
												onClick={() => setSelectedFieldKey(f.field_key)}>
												<span className="text-start small">
													{f.name ||
														t(f.field_key, {
															defaultValue: FIELD_LABELS[f.field_key] || f.field_key,
														})}
												</span>
												{shouldShowVisibilityStatusIcon(f) &&
													(f.is_show ? (
														<i className="uil uil-check text-primary"></i>
													) : (
														<i className="uil uil-times text-muted"></i>
													))}
											</button>
										))}
									</div>
								</div>
							</div>
						</div>

						<div
							className="w-100"
							style={{
								height: "100%",
								minHeight: 0,
								minWidth: 0,
								display: "flex",
								flexDirection: "column",
							}}>
							<div className="card h-100 mb-0" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
								<div className="card-body" style={{ overflowY: "auto", flex: 1, minHeight: 0, paddingBottom: "16px" }}>
									{selectedField ? (
										<>
											{fieldUsesVisibilityToggle(selectedField) &&
												hasEditorSection(selectedField.has_is_show, true) && (
													<div className="form-check form-switch mb-4">
														<input
															className="form-check-input"
															type="checkbox"
															id="field-is-show"
															checked={!!fieldPreviewIsShown(selectedField)}
															onChange={(e) =>
																updateField(activeGroupValue, selectedField.field_key, {
																	is_show: e.target.checked,
																})
															}
														/>
														<label className="form-check-label" htmlFor="field-is-show">
															Отображать
														</label>
													</div>
												)}

											{showAlignFont && (
												<>
													{showAlignSection && alignOptions.length > 0 && (
														<div className="mb-3">
															<label className="form-label">Позиция</label>
															<Select
																options={alignSelectOptions}
																value={selectedAlignOption}
																onChange={(option) =>
																	updateField(activeGroupValue, selectedField.field_key, {
																		align: option?.value ?? "left",
																	})
																}
																placeholder=""
																noOptionsMessage={() => t("list_empty")}
																getOptionLabel={(option) => option.label}
																getOptionValue={(option) => option.value}
																isClearable={false}
															/>
														</div>
													)}
													{showFontSection && fontOptions.length > 0 && (
														<div className="mb-3">
															<label className="form-label">Шрифт</label>
															<Select
																options={fontSelectOptions}
																value={selectedFontOption}
																onChange={(option) =>
																	updateField(activeGroupValue, selectedField.field_key, {
																		font_size: option?.value ?? "small",
																	})
																}
																placeholder=""
																noOptionsMessage={() => t("list_empty")}
																getOptionLabel={(option) => option.label}
																getOptionValue={(option) => option.value}
																isClearable={false}
															/>
														</div>
													)}
												</>
											)}

											{showOrderInput && (
												<div className="mb-3">
													<label className="form-label">Строка (порядок)</label>
													<input
														type="number"
														min={1}
														className="form-control"
														value={
															selectedField.order_number ??
															selectedField.default_order_number ??
															""
														}
														onChange={(e) =>
															updateField(activeGroupValue, selectedField.field_key, {
																order_number: e.target.value === "" ? null : Number(e.target.value),
															})
														}
													/>
												</div>
											)}

											{showTextValueInput && (
												<div className="mb-3">
													<label className="form-label">{t("text")}</label>
													<textarea
														className="form-control"
														rows={3}
														placeholder={selectedField.text_placeholder ?? ""}
														maxLength={textMaxLen}
														value={selectedField.text_value ?? ""}
														onChange={(e) => {
															let next = e.target.value
															if (textMaxLen != null && next.length > textMaxLen) {
																next = next.slice(0, textMaxLen)
															}
															updateField(activeGroupValue, selectedField.field_key, {
																text_value: next,
															})
														}}
													/>
													{textMaxLen != null && (
														<div className="small text-muted mt-1">
															{(selectedField.text_value ?? "").length} / {textMaxLen}
														</div>
													)}
												</div>
											)}
										</>
									) : (
										<p className="text-muted mb-0">Выберите поле слева</p>
									)}
								</div>
							</div>
						</div>

						<div
							style={{
								minWidth: "320px",
								maxWidth: "420px",
								flex: "0 0 380px",
								height: "100%",
								minHeight: 0,
								overflow: "hidden",
							}}>
							<div style={{ height: "100%", overflowY: "auto", paddingRight: "2px", paddingBottom: "16px" }}>
								<ChequePreviewPanel
										groups={data.groups}
										groupValue={activeGroupValue}
										selectedFieldKey={selectedFieldKey}
									/>
							</div>
						</div>
					</div>
				</div>

				<div className={mainActiveTab !== 2 ? "d-none" : ""}>
					<div className="card mb-3">
						<div className="card-body py-3">
							<div className="row g-3">
								<div className="col-md-6">
									<div className="form-group w-100">
										<label className="form-label">
											{t("name2")}
											<span className="required-mark">*</span>
										</label>
										<input
											type="text"
											required
											className="form-control"
											value={data.name ?? ""}
											onChange={(e) => setData({ ...data, name: e.target.value })}
										/>
									</div>

									<div className="form-check mt-2">
										<input
											id="cheque-template-is-active"
											className="form-check-input"
											type="checkbox"
											checked={!!data.is_active}
											onChange={(e) => setData({ ...data, is_active: e.target.checked })}
										/>
										<label className="form-check-label" htmlFor="cheque-template-is-active">
											{t("active")}
										</label>
									</div>
									<div className="form-check mt-2">
										<input
											id="cheque-template-is-default"
											className="form-check-input"
											type="checkbox"
											checked={!!data.is_default}
											onChange={(e) => setData({ ...data, is_default: e.target.checked })}
										/>
										<label className="form-check-label" htmlFor="cheque-template-is-default">
											{t("default")}
										</label>
									</div>
								</div>
								<div className="col-6">
									<label className="form-label">{t("comment")}</label>
									<textarea
										className="form-control"
										rows={4}
										value={data.comment ?? ""}
										onChange={(e) => setData({ ...data, comment: e.target.value })}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className={mainActiveTab !== 3 ? "d-none" : ""}>
					<div className="card mb-3">
						<div className="card-body">
							<input
								type="search"
								className="form-control mb-3"
								placeholder={t("search")}
								value={posSearch}
								onChange={(e) => setPosSearch(e.target.value)}
							/>
							<div className="small text-muted mb-2">Выбрано: {selectedPosCount}</div>
							<div className="table-responsive">
								<table className="table table-bordered mb-0 align-middle">
									<thead className="table-light">
										<tr>
											<th style={{ width: 40 }}>
												<input
													disabled
													type="checkbox"
													className="form-check-input"
													checked={allFilteredSelected}
													onChange={(e) => setAllFilteredChecked(e.target.checked)}
												/>
											</th>
											<th>{t("code")}</th>
											<th>{t("name2")}</th>
											<th>{t("address")}</th>
											<th className="text-center">{t("status")}</th>
										</tr>
									</thead>
									<tbody>
										{filteredPosList.map((p) => (
											<tr key={p.id}>
												<td>
													<input
														disabled
														type="checkbox"
														className="form-check-input"
														checked={(data.pos_ids || []).map(Number).includes(Number(p.id))}
														onChange={() => togglePosId(p.id)}
													/>
												</td>
												<td className="text-nowrap">{p.id}</td>
												<td className="td-ellipsis">{p.name}</td>
												<td className="td-ellipsis small">{p.address}</td>
												<td className="text-center small">{posStatusLabel(p, t)}</td>
											</tr>
										))}
										{!filteredPosList.length && (
											<tr>
												<td colSpan={5} className="text-center text-muted py-4">
													{t("list_empty")}
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</form>
		</>
	)
}

export default CreateUpdate
